import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Divider, Dropdown, Form, Icon, Input, Label } from 'semantic-ui-react';
import AuthService, { SendSmsGetDataType } from '~/api/services/auth';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { ThemeContext } from 'styled-components';
import LayoutAuth from './LayoutAuth';
import { El } from './style';
import { FARMER_ROLE_SLUG, passwordRegexWithoutSpecChar } from '~/Constants';
import dayjs from 'dayjs';
import i18n from '~/components/i18n';
import { handleErrors } from '~/utils/helpers';
import TwoFaField from './TwoFaField';

const passwordRegex = /^(?=.*[0-9])(?=.*[^\p{L}\d])(?=.*[A-Z])(?=.*[a-z])/u
const MINIMUM_LENGTH_PASSWORD = 8;
const MAX_LENGTH_PASSWORD = 32;

interface PasswordFormProps {
  resetPassword?: boolean
}

interface ResetPasswordDataType {
  passwordSecurity: boolean
  tfaType: string
  mobileNumber?: string
}

const PasswordForm: React.FC<PasswordFormProps> = (props) => {
  const { resetPassword } = props
  const { t } = useTranslation()
  const { code } = useParams();
  const navigate = useNavigate()
  const { logoTitle, firstColor } = useContext(ThemeContext)
  const settingsData = useContext(ThemeContext)
  const matchPasswordErrorMessage = t('Passwords do not match');

  const [isBusy, setIsBusy] = useState(false)
  const [sendingTwoFaCode, setSendingTwoFaCode] = useState(false)
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleRepeatPassword, setVisibleRepeatPassword] = useState(false);
  const [registrationInfo, setRegistrationInfo] = useState<SendSmsGetDataType>(null)
  const [registrationInfoReset, setRegistrationInfoReset] = useState<ResetPasswordDataType>(null)
  const [tfaType, setTfaType] = useState('')
  const [mobileNumber, setMobileNumber] = useState<string | number | boolean | (string | number | boolean)[]>('')
  const [mobilePrefix, setMobilePrefix] = useState<string | number | boolean | (string | number | boolean)[]>('')
  const [correctTfaCode, setCorrectTfaCode] = useState(false)

  const { handleSubmit, control, getValues, watch } = useForm({
    mode: 'onChange'
  });

  const watchTermsAccepted = watch('terms_accepted')

  useEffect(() => {
    checkLinkToVerifyUser()
  }, [])

  async function checkLinkToVerifyUser() {
    setIsBusy(true);
    try {
      let response;
      const languageStored = localStorage.getItem('i18nextLng') || 'de'
      let responseData
      if (!resetPassword) {
        response = await AuthService.setPasswordCheck({ code });
        responseData = await AuthService.sendSmsGetData({ code })
        dayjs.locale(responseData.data?.user?.languageCode || languageStored)
        i18n.changeLanguage(responseData.data?.user?.languageCode || languageStored)
        const deMobilePrefix = responseData.data?.countries?.find(country => country?.iso === 'de')
        setMobilePrefix(deMobilePrefix?.id)
        setRegistrationInfo(responseData.data)
        setTfaType(responseData.data.user.tfaType)
      } else {
        response = await AuthService.resetPasswordCheck({ code });
        responseData = await AuthService.resetPasswordData({ code })
        dayjs.locale(responseData.data?.languageCode || languageStored)
        i18n.changeLanguage(responseData.data?.languageCode || languageStored)
        setRegistrationInfoReset(responseData.data)
        setTfaType(responseData.data.tfaType)
      }
      if (response.data.message) {
        navigate(RouteTypes.LOGIN)
        notify(t('Set link password expired'), 'error');
      }
    } catch (error) {
      navigate(RouteTypes.LOGIN)
      notify(t('Set link password expired'), 'error');
    }
    setIsBusy(false);
  }

  async function onSubmitPassword(data) {
    data.code = code;
    setIsBusy(true);
    try {
      if (!resetPassword) {
        await AuthService.setPassword(data);
      } else {
        await AuthService.resetPassword(data);
      }
      navigate(RouteTypes.LOGIN);
      notify(t('Password set'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderCheckTerms() {
    return <FormFields.Checkbox
      label={<label style={{ color: '#5e6672', fontWeight: 700 }}>
        {settingsData?.files.map((file, index) => `${file.name || file.label}${index !== settingsData.files.length - 1 ? ', ' : ''}`)}
      </label>}
      control={control}
      name={'terms_accepted'}
      rules={{
        required: {
          value: true,
          message: (t('Please accept terms of use'))
        }
      }}
      defaultValue={0}
      checkboxValue={1}
    />
  }

  async function sendCodeWithMobile() {
    setSendingTwoFaCode(true)
    try {
      await AuthService.saveMobileNumber({
        mobileNumber,
        mobilePrefix,
        code
      })
      notify(t('Code was sent'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setSendingTwoFaCode(false)
  }

  async function handleResendCode() {
    setIsBusy(true);
    try {
      if (!resetPassword) {
        await AuthService.resendSetPasswordCode({ code });
      } else {
        await AuthService.resendResetPasswordCode({ code });
      }
      notify(t('Code was sent'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function getVerificationLabel() {
    switch (tfaType) {
      case 'email':
        return t('E-Mail')
      case 'sms':
        return t('SMS')
      case 'google':
        return t('Authenticator')
      default:
        return ''
    }
  }

  function renderNewMobileNumber() {
    return <Form.Field style={{ paddingLeft: 0 }} required>
      <label>{t('Mobile number')}</label>
      <Input
        style={{ minWidth: 250 }}
        value={mobileNumber}
        label={<Dropdown
          scrolling
          basic
          options={registrationInfo?.countries.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
          selectOnBlur={false}
          onChange={(e, data) => setMobilePrefix(data.value)}
          value={mobilePrefix || ''}
          placeholder={t('Code')}
        />}
        onChange={(e, data) => setMobileNumber(data.value)}
        labelPosition='left'
        placeholder={mobilePrefix ? t('Mobile number') : t('Please select mobile code')}
        type={'number'}
        name={'mobileNumber'}
      />
    </Form.Field>
  }

  function checkIfMobileIsEntered(noPredefinedNumber: boolean) {
    if (noPredefinedNumber) {
      // since you cannot have correct two fa code without entering mobile phone and prefix
      // correctTfa code is here enough to validate
      return !correctTfaCode
    }
    return false
  }

  function renderUserDetails() {
    return <Form.Group >
      <Form.Field width={8}>
        <label>{t('First name:')}</label>
        <El.StyledLabelInfo>
          <Icon name='user' />
          <Label.Detail>{`${registrationInfo?.user.firstName}`}</Label.Detail>
        </El.StyledLabelInfo>
      </Form.Field>
      <Form.Field width={8}>
        <label>{t('Last name:')}</label>
        <El.StyledLabelInfo >
          <Icon name='user' />
          <Label.Detail>{`${registrationInfo?.user.lastName}`}</Label.Detail>
        </El.StyledLabelInfo>
      </Form.Field>
    </Form.Group>
  }

  function renderDairyDetails() {
    return <>
      <div style={{ textAlign: 'initial' }}>
        <Form.Group>
          <Form.Field width={8}>
            <label>{t('Supplier no.')}</label>
            <El.StyledLabelInfo>
              <Icon name='factory' />
              <Label.Detail>{`${registrationInfo?.user?.supplierNo || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          <Form.Field width={8}>
            <label>{t('Member')}</label>
            <El.StyledLabelInfo>
              <Icon name='address book outline' />
              <Label.Detail>{`${registrationInfo?.user?.companyName || ''}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
        </Form.Group>
      </div>
      <Divider section style={{ marginBottom: '1rem', color: firstColor }} />
    </>
  }

  function renderActions(enableCodeField, mobileNumberNotPreset) {
    return <Form.Group style={{ justifyContent: 'center', width: '110%' }}>
      {tfaType !== 'google' && enableCodeField && <Form.Field style={El.loginFormSubmitWrapper}>
        <Button
          onClick={() => mobileNumberNotPreset ? sendCodeWithMobile() : handleResendCode()}
          style={El.loginFormSubmitButton}
          type={'button'}
          primary
          loading={sendingTwoFaCode}
          content={t('Send 2FA code')}
          disabled={mobileNumberNotPreset ? mobileNumber === '' || mobilePrefix === '' || sendingTwoFaCode : false}
        />
      </Form.Field>}
      <Form.Field style={El.loginFormSubmitWrapper}>
        <Button
          style={El.loginFormSubmitButton}
          primary
          disabled={(registrationInfo && settingsData?.files?.length > 0 && !watchTermsAccepted) || checkIfMobileIsEntered(mobileNumberNotPreset)}
          content={resetPassword ? t('Reset password') : t('Set')}
        />
      </Form.Field>
    </Form.Group>
  }

  function renderPasswordFields() {
    const specialCharPasswordReq = registrationInfo?.dairySettings.passwordSecurity || registrationInfoReset?.passwordSecurity
    return <>
      <FormFields.Input
        control={control}
        style={El.loginFormField}
        label={t('Assign new password')}
        styleLabel={{ marginTop: '-14px', position: 'relative' }}
        name='password'
        size='large'
        type={visiblePassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visiblePassword ? 'eye slash' : 'eye'}
            onClick={() => setVisiblePassword(!visiblePassword)}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter password')
          },
          pattern: {
            value: specialCharPasswordReq ? passwordRegex : passwordRegexWithoutSpecChar,
            message: specialCharPasswordReq ?
              t('The password must contain at least one uppercase letter, one lowercase letter, one special character and one number. In addition, the password must be at least 8 characters long.') :
              t('The password must contain at least one uppercase letter, one lowercase letter, and one number. The password must also be at least 8 characters long.')
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
        placeholder={t('Password')}
      />
      <FormFields.Input
        style={El.loginFormField}
        label={t('Confirm new Password')}
        styleLabel={{ marginTop: '-14px', position: 'relative' }}
        name='passwordConfirmation'
        type={visibleRepeatPassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visibleRepeatPassword ? 'eye slash' : 'eye'}
            onClick={() => setVisibleRepeatPassword(!visibleRepeatPassword)}
          />
        }
        size='large'
        rules={{
          required: {
            value: true,
            message: t('Please enter password confirmation')
          },
          validate: (value) => getValues('password') === value || matchPasswordErrorMessage
        }}
        placeholder={t('Password confirmation')}
        control={control}
      />
    </>
  }

  function renderTwoFa() {
    const verificationLabel = registrationInfo?.dairySettings.smsVerificationRegistration ? t('SMS') : getVerificationLabel()
    return <TwoFaField
      control={control}
      reset={resetPassword}
      keyName={'pinCode'}
      verificationLabel={verificationLabel}
      onChangeCorrectTwoFaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
    />
  }

  const renderPasswordForm = () => {
    const enableCodeField = registrationInfo?.dairySettings.smsVerificationRegistration || registrationInfoReset
    const mobileNumberNotPreset = registrationInfo?.user.mobileSet === 0 && registrationInfo?.dairySettings?.smsVerificationRegistration === 1
    const displayNumber = Boolean(registrationInfo?.dairySettings?.showMobileOnVerification) && registrationInfo?.user.mobileSet === 1
    return (
      <Form
        style={El.loginForm}
        onSubmit={handleSubmit(onSubmitPassword)}
        autoComplete={'off'}
      >
        <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
        <El.FormLogo style={{ color: firstColor }}>{settingsData?.headerTitle || ''}</El.FormLogo>
        <Divider section />
        {registrationInfo?.user && registrationInfo?.user.roleSlug === FARMER_ROLE_SLUG && renderDairyDetails()}
        <El.FormHeading style={{ marginBottom: '1rem', color: firstColor, fontWeight: 'bold' }}>
          {t('User\'s info')}
        </El.FormHeading>
        <div style={{ textAlign: 'initial' }}>
          {registrationInfo?.user.roleSlug === FARMER_ROLE_SLUG && renderUserDetails()}
          {displayNumber && <Form.Field size='large'>
            <label>{t('Mobile number:')}</label>
            <El.StyledLabelInfo>
              <Icon name='phone' />
              <Label.Detail>{`${registrationInfo?.user.phoneCode} ${registrationInfo?.user.mobileNumber}`}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          }
          {mobileNumberNotPreset && renderNewMobileNumber()}
          {renderPasswordFields()}
          {registrationInfoReset?.tfaType === 'sms' && registrationInfoReset?.mobileNumber && <Form.Field size='large' style={{ marginBottom: '2em' }}>
            <label>{t('Mobile number:')}</label>
            <El.StyledLabelInfo>
              <Icon name='phone' />
              <Label.Detail>{registrationInfoReset?.mobileNumber}</Label.Detail>
            </El.StyledLabelInfo>
          </Form.Field>
          }
          {enableCodeField && renderTwoFa()}
          {settingsData?.files?.length > 0 && registrationInfo && renderCheckTerms()}
        </div>
        {renderActions(enableCodeField, mobileNumberNotPreset)}
        <Divider section style={{ marginBottom: 0 }} />
      </Form>
    );
  };

  return <LayoutAuth isLoading={isBusy}>
    {renderPasswordForm()}
  </LayoutAuth>
}
export default PasswordForm
